import _ from 'lodash'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { CreatePatientDto } from '@/core/models/patient.model'
import { useSearchPatientsQuery } from '@/core/services/patient'

import { useIsSA } from '@/hooks/useIsSA'

import SelectPatientStep from '@/pages/create-consultation/components/SelectPatientStep'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { clearNewlyCreatedPatientId, createPatient, selectNewlyCreatedPatientId } from '@/redux/reducers/patient.reducer'

import { CreateConsultationStepperContext } from '../CreateConsultationStepper'
import CreateConsultationStepLayout from '../components/CreateConsultationStepLayout'

export function SelectPatientStepContainer() {
  const { previousStep, setValue, values } = useContext(CreateConsultationStepperContext)

  const dispatch = useAppDispatch()
  const isSA = useIsSA()
  const authUser = useAppSelector(selectAuthUser)
  const newlyCreatedPatientId = useAppSelector(selectNewlyCreatedPatientId)

  const [searchPatientsArgs, setSearchPatientsArgs] = useState({ patientSource: PatientSourceEnum.MedconnectPatient, orderBy: 'userInfo.surname' })
  // The emergency patient is created on the fly and is usually absent from the current
  // page of the patient table, so the table row cannot carry the selected state. Remember
  // its id to drive the emergency button's own selected state instead.
  const [emergencyPatientId, setEmergencyPatientId] = useState<string>()
  const emergencyRequestedRef = useRef(false)
  // Cache selected patient names so they persist across page changes
  const selectedPatientNamesRef = useRef<Record<string, string>>({})

  const {
    isFetching: isFetchingPatients,
    patients,
    totalPatients,
  } = useSearchPatientsQuery(searchPatientsArgs, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      patients:
        data?.items.map((patient) => ({
          _id: patient._id,
          name: patient.userInfo.name,
          surname: patient.userInfo.surname,
          gender: patient.userInfo.gender,
          birthday: patient.userInfo.birthday,
          city: patient.contactInfo?.city,
          secu: patient.secu,
        })) ?? [],
      totalPatients: data?.totalCount ?? 0,
    }),
  })

  useEffect(() => {
    if (previousStep === 0) return
    if (values.patient) setValue('patient', undefined)
    if (values.patients) setValue('patients', undefined)
    // eslint-disable-next-line
  }, [])

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, limit: rowsPerPage, skip: (newPage - 1) * rowsPerPage }))
  }

  const handleSelectPatient = (patientId: string) => {
    if (values.isGroupConsultation) {
      const currentPatients = values.patients || []
      const isAlreadySelected = currentPatients.includes(patientId)
      if (!isAlreadySelected) {
        // Cache the patient name on selection
        const patient = patients.find((p) => p._id === patientId)
        if (patient) selectedPatientNamesRef.current[patientId] = `${patient.name} ${patient.surname}`
      } else {
        delete selectedPatientNamesRef.current[patientId]
      }
      const updatedPatients = isAlreadySelected
        ? currentPatients.filter((id) => id !== patientId)
        : [...currentPatients, patientId]
      setValue('patients', updatedPatients as any)
      // Also set first patient for backward compatibility
      if (updatedPatients.length > 0) setValue('patient', updatedPatients[0])
      else setValue('patient', undefined)
    } else {
      setValue('patient', patientId)
    }
  }

  const handleCreateEmergencyPatient = () => {
    emergencyRequestedRef.current = true
    const createPatientDto: CreatePatientDto = {
      agreement: 'Emergency',
      userInfo: {
        birthday: new Date().toISOString(),
        gender: 'I',
        name: new Date().toLocaleString().replace(/[/+:,\s]+/g, ''),
        surname: isSA ? 'Emergency' : 'Urgence',
      },
      active: false,
      // added logic to create emergency patient when italian language is selected with secu length 16
      secu: authUser?.userInfo?.language === 'it' ? Math.random().toString().substring(2, 18) : Math.random().toString().substring(2, 17), // secu is required
    }
    dispatch(createPatient(createPatientDto))
  }

  useEffect(() => {
    if (!newlyCreatedPatientId) return
    if (emergencyRequestedRef.current) {
      setEmergencyPatientId(newlyCreatedPatientId)
      emergencyRequestedRef.current = false
    }
    if (values.isGroupConsultation) {
      const currentPatients = values.patients || []
      if (!currentPatients.includes(newlyCreatedPatientId)) {
        const updatedPatients = [...currentPatients, newlyCreatedPatientId]
        setValue('patients', updatedPatients as any)
        setValue('patient', updatedPatients[0])
      }
    } else {
      setValue('patient', newlyCreatedPatientId)
    }
    dispatch(clearNewlyCreatedPatientId())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newlyCreatedPatientId])

  const handleToggleGroupConsultation = (isGroup: boolean) => {
    setValue('isGroupConsultation', isGroup)
    if (!isGroup) {
      setValue('patients', undefined)
      // Keep the first selected patient if there was one
      if (values.patients && values.patients.length > 0) {
        setValue('patient', values.patients[0])
      }
    } else {
      // Switch to group mode: if a patient was already selected, start the list with it
      if (values.patient) {
        setValue('patients', [values.patient] as any)
      }
    }
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const smartSearch = event.target.value.replace(/[-']/g, ' ').replace(/\s+/g, ' ').trim()
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, smartSearch, skip: 0 }))
  }

  const redirectToPatientProfile = (patientId: string) => {
    window.open(`/patients/${patientId}`, '_blank')
  }

  // Selected either as the single patient or as one of the group consultation's patients.
  const isEmergencyPatientSelected =
    !!emergencyPatientId && (values.isGroupConsultation ? !!values.patients?.includes(emergencyPatientId) : values.patient === emergencyPatientId)

  const isNextDisabled = values.isGroupConsultation
    ? !values.patients || values.patients.length < 2
    : !values.patient

  return (
    <CreateConsultationStepLayout nextButtonDisabled={isNextDisabled}>
      <SelectPatientStep
        loading={isFetchingPatients}
        multiSelect={values.isGroupConsultation}
        onToggleGroupConsultation={handleToggleGroupConsultation}
        selectedPatientIds={values.isGroupConsultation ? values.patients : values.patient ? [values.patient] : []}
        selectedPatientNamesMap={selectedPatientNamesRef.current}
        emergencyPatientSelected={isEmergencyPatientSelected}
        onCreateEmergencyPatient={handleCreateEmergencyPatient}
        onPageChange={handleChangePage}
        onSearch={handleSearch}
        onSelectPatient={handleSelectPatient}
        onSelectPatientSource={(patientSource) => {
          setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, skip: 0, patientSource }))
        }}
        patients={patients}
        redirectToPatientProfile={redirectToPatientProfile}
        totalPatients={totalPatients}
      />
    </CreateConsultationStepLayout>
  )
}

export default SelectPatientStepContainer
